import * as api from '../core/api/api';
import * as cmxDateTime from '../core/date-time/cmx-date-time';
import * as commonActions from './action-types';
import * as dictionaries from '../core/dictionaries';
import * as commonEnums from '../enums/index';
import * as documentUtils from '../core/document-utils';
import HttpMethods from '../enums/http-methods';
import { gradeBenchStateToResolverInput } from '../core/resolver';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { convertToDateFormat } from '../core/date-time/cmx-date-time';
import { codePredicates } from '../core/amplify/transaction-updates.dto-factory';
import {
  loadNextOrPreviousCase,
  loadWorkitemInAmplify,
  showSubmittedFeedback
} from './code-bench.actions';
import { amplifyFeedback } from './ui.actions';
import { buildQualityAssessmentWorklistPath } from './quality-assessment-navigation';
import { navigationIntent } from './navigation-intent.action';
import { getOptionsFromTree } from '../core/workbench/generalized-form/path-utils';
const { isSelectionCPT, isSelectionICD, isSelectionModifier } = codePredicates;
import { uniqBy } from '../core/object-utils';
import { CoderNotesLookupTypeName } from '../enums';

const { GradeBenchPanel } = commonActions;

type CQAError = { reason: string; status: number };

const fetchRiskCategories = createAsyncThunk<CMxAPI.RiskCategoryData, any, { rejectValue: any }>(
  GradeBenchPanel.FETCH_RISK_CATEGORIES,
  async () => {
    const todaysDate = convertToDateFormat(new Date());
    const ModRowData = await api.requestJSON<CMxAPI.RowData>(
      `/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesMODV2`,
      {
        method: HttpMethods.GET
      }
    );

    const CptRowData = await api.requestJSON<CMxAPI.RowData>(
      `/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesCPT`,
      {
        method: HttpMethods.GET
      }
    );

    const IcdRowData = await api.requestJSON<CMxAPI.RowData>(
      `/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesICD`,
      {
        method: HttpMethods.GET
      }
    );

    return {
      ModRowData,
      CptRowData,
      IcdRowData
    };
  }
);

/*
we expect context to be
  { value : object
  field : CMxCommonApp.CodeLayerField,
  fields : CMxCommonApp.CodeLayerField[],
  transaction : CMxCommonApp.CQATransaction
  invoice : CMxCommonApp.CQAInvoice }
*/
const gradeUpdate = createAsyncThunk<
  null,
  CMxCommonApp.GradeBenchSelectEvent,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.GRADE_UPDATE, async (changeEvent, { dispatch }) => {
  const { field } = changeEvent.context;

  if (field.key == 'assessment' || field.key == 'role') {
    dispatch(updateOrAddAssessment(changeEvent) as any);
  }

  return null;
});

const updateOrAddAssessment = createAsyncThunk<
  Accelerate.CQACodeAssessment | null,
  CMxCommonApp.GradeBenchSelectEvent,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.UPDATE_OR_ADD_ASSESSMENT,
  async (changeEvent, { dispatch }) => {
    const { field, fields, value } = changeEvent.context;
    let assessment = '';
    let role = '';
    try {
      if (field.key === 'assessment') {
        assessment = changeEvent.selection;
        const roleField: CMxCommonApp.SelectListField = fields.find(
          field => field.key == 'role'
        );
        let optionsAtTree = getOptionsFromTree(
          { ...value, codeAssessment: { assessment } },
          roleField.optionsTree,
          roleField.optionsTreePath
        );
        if (optionsAtTree && optionsAtTree.length == 1) {
          role = optionsAtTree[0];
        }
      }

      if (field.key === 'role') {
        assessment = value?.codeAssessment?.assessment
          ? value?.codeAssessment?.assessment
          : null;
        role = changeEvent.selection;
      }

      if (value.codeAssessment === null) {
        return await api.requestJSON<Accelerate.CQACodeAssessment>(
          `/api/cqa-service/v1/${value.codeType}/${value.id}/assessment`,
          {
            method: HttpMethods.POST,
            body: JSON.stringify({ assessment, role })
          }
        );
      } else if (
        value.codeAssessment.role !== role ||
        value.codeAssessment.assessment !== assessment
      ) {
        return await api.requestJSON<Accelerate.CQACodeAssessment>(
          `/api/cqa-service/v1/assessment/${value.codeAssessment.id}`,
          {
            method: HttpMethods.PUT,
            body: JSON.stringify({ assessment, role })
          }
        );
      }
    } catch (e) {
      console.log('e', e);
    }

    return null;
  }
);

const addCode = createAsyncThunk<
  Accelerate.CodeAddActionPayload,
  Accelerate.CodeAddActionPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.ADD_CODE, async (addCodePayload, { dispatch }) => {
  if (isSelectionCPT(addCodePayload.codeInfo)) {
    await dispatch(addProcedure(addCodePayload) as any);
  }

  if (isSelectionICD(addCodePayload.codeInfo)) {
    await dispatch(addDiagnosis(addCodePayload) as any);
  }

  if (isSelectionModifier(addCodePayload.codeInfo)) {
    await dispatch(addModifier(addCodePayload) as any);
  }

  return addCodePayload;
});

const addProcedure = createAsyncThunk<
  Accelerate.CQAProcedure,
  Accelerate.CodeAddActionPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.ADD_PROCEDURE_CODE, async (addCodePayload, { dispatch }) => {
  const requestBody: any = {
    code: addCodePayload.codeInfo.code,
    units: 1,
    sequenceNumber: 0
  };

  // Add spans information if provided
  if (addCodePayload.spans && addCodePayload.documentId && addCodePayload.caseId) {
    requestBody.documentId = Number(addCodePayload.documentId);
    requestBody.caseId = addCodePayload.caseId;
    requestBody.spans = addCodePayload.spans.map(span => ({
      id: span.id, // Include id if span already exists (backend will use existing span)
      beginOffset: span.beginOffset,
      endOffset: span.endOffset,
      coveredText: span.coveredText,
      attributes: span.attributes
    }));
  }

  return await api.requestJSON<Accelerate.CQAProcedure>(
    `/api/cqa-service/v1/transaction/${addCodePayload.context.transaction.id}/procedure`,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(requestBody)
    }
  );
});

const addDiagnosis = createAsyncThunk<
  Accelerate.CQADiagnosis,
  Accelerate.CodeAddActionPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.ADD_DIAGNOSIS_CODE, async (addCodePayload, { dispatch }) => {
  const requestBody: any = {
    code: addCodePayload.codeInfo.code,
    sequenceNumber: 0
  };

  // Add spans information if provided
  if (addCodePayload.spans && addCodePayload.documentId && addCodePayload.caseId) {
    requestBody.documentId = Number(addCodePayload.documentId);
    requestBody.caseId = addCodePayload.caseId;
    requestBody.spans = addCodePayload.spans.map(span => ({
      id: span.id, // Include id if span already exists (backend will use existing span)
      beginOffset: span.beginOffset,
      endOffset: span.endOffset,
      coveredText: span.coveredText,
      attributes: span.attributes
    }));
  }

  return await api.requestJSON<Accelerate.CQADiagnosis>(
    `/api/cqa-service/v1/transaction/${addCodePayload.context.transaction.id}/diagnosis`,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(requestBody)
    }
  );
});

const addModifier = createAsyncThunk<
  Accelerate.CQAModifier,
  Accelerate.CodeAddActionPayload,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.ADD_MODIFIER_CODE, async (addCodePayload, { dispatch }) => {
  const requestBody: any = {
    code: addCodePayload.codeInfo.code,
    sequenceNumber: 0
  };

  // Add spans information if provided
  if (addCodePayload.spans && addCodePayload.documentId && addCodePayload.caseId) {
    requestBody.documentId = Number(addCodePayload.documentId);
    requestBody.caseId = addCodePayload.caseId;
    requestBody.spans = addCodePayload.spans.map(span => ({
      id: span.id, // Include id if span already exists (backend will use existing span)
      beginOffset: span.beginOffset,
      endOffset: span.endOffset,
      coveredText: span.coveredText,
      attributes: span.attributes
    }));
  }

  return await api.requestJSON<Accelerate.CQAModifier>(
    `/api/cqa-service/v1/transaction/${addCodePayload.context.transaction.id}/modifier`,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(requestBody)
    }
  );
});

const removeCode = createAction<Accelerate.GradeUpdate>(
  GradeBenchPanel.REMOVE_CODE
);

const resequenceCode = createAsyncThunk<
  CMxCommonApp.CQACode[] | null,
  CMxCommonApp.GradeBenchCodeEvent,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.CODE_EVENT, async (changeEvent, { dispatch }) => {
  const code = changeEvent.code;
  try {
    const codeType = code.codeType;
    const units = (code as Accelerate.CQAProcedure).units ?? undefined;
    return await api.requestJSON<CMxCommonApp.CQACode[]>(
      `/api/cqa-service/v1/${codeType}/${code.id}`,
      {
        method: HttpMethods.PUT,
        body: JSON.stringify({
          code: code.code,
          sequenceNumber: code.sequenceNumber,
          units: codeType == 'procedure' ? units : undefined
        })
      }
    );
  } catch (e) {
    console.log(e);
  }

  return null;
});

type SimpleParams = {
  params: {
    [key: string]: string;
  };
};

const loadCQAItemFromURL = createAsyncThunk<
  Accelerate.CQAGraderWorkItem,
  SimpleParams,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.LOAD_CQA_WORK_ITEM_FROM_URL, async (match, { dispatch }) => {
  const cqaGraderWorkItem = await api.requestJSON<Accelerate.CQAGraderWorkItem>(
    `/api/cqa-service/v1/grader-workitem/${match.params.id}`,
    {
      method: HttpMethods.GET
    }
  );
  dispatch(getDocumentsAsHtmlFromCQAWorkItem(cqaGraderWorkItem.workItem) as any);
  dispatch(fetchCodeDescriptions(cqaGraderWorkItem) as any);
  dispatch(retrieveAssociatedWorkitems(cqaGraderWorkItem.workItem.id) as any);

  // Fetch all rubric attributes for case, codes, and spans in one action
  dispatch(fetchAllAttributes({
    sdCaseId: cqaGraderWorkItem.sdCaseId,
    invoices: cqaGraderWorkItem.invoices,
    looseSpans: cqaGraderWorkItem.looseSpans || []
  }) as any);

  const { ruleCollectionId, serviceLine } = cqaGraderWorkItem.workItem;
  if (ruleCollectionId && serviceLine) {
    dispatch(
      fetchQaRule({
        qaCollectionUuid: ruleCollectionId,
        level: 3,
        service: serviceLine
      }) as any
    );
  }

  return cqaGraderWorkItem;
});

const fetchQaRule = createAsyncThunk<
  CMxAPI.QaRule | null,
  Accelerate.FetchQaRuleParams,
  { rejectValue: null }
>(GradeBenchPanel.FETCH_QA_RULE, async (qaRuleParams, { dispatch }) => {
  const { qaCollectionUuid, level, service } = qaRuleParams;

  const url = '/api/service-desk/bff';
  const query = {
    operationName: 'SearchQarule',
    variables: {
      request: {
        combinator: 'AND',
        filters: [
          {
            key: 'collection_id',
            operation: 'eq',
            value: qaCollectionUuid
          }
        ],
        level
      }
    },
    query:
      'mutation SearchQarule($request: SearchQaruleRequest!) {\n  SearchQarule(request: $request) {\n    data\n    total\n    __typename\n  }\n}'
  };

  try {
    const response = await api.requestJSON<CMxAPI.QaRuleResponse>(url, {
      body: JSON.stringify(query),
      method: 'POST'
    });

    const total = response.data.SearchQarule.total;
    const qaRule = total >= 1 ? response.data.SearchQarule.data[0] : null;

    // Always fetch rubric from project configuration when qaRule is loaded
    const rubricVersionId = qaRule?.data?.project_configuration?.rubricVersionId;
    if (rubricVersionId) {
      dispatch(fetchRubricVersion({ versionId: rubricVersionId }) as any);
    } else {
      dispatch(clearRubricVersion());
    }

    return qaRule;
  } catch (error) {
    console.error('Failed to SearchQarule in fetchQaRule fn', error);
  }

  return null;
});

const collapseWhitespace = (text: string): string => {
  return (
    text
      // First normalize all line endings to \n
      .replace(/\r\n/g, '\n')
      // Replace 3 or more newlines with exactly 2 newlines (preserving paragraph spacing)
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
};

const getDocumentsAsHtmlFromCQAWorkItem = createAsyncThunk<
  CMxCommonApp.DecoratedNote[],
  Accelerate.CQAWorkItem,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  GradeBenchPanel.FETCH_CQA_WORKITEM_DOCUMENTS,
  async (cqaWorkItem, { dispatch }) => {
    let newDocs = [];
    let notes: CMxCommonApp.DecoratedNote[] = [];

    for (let note of cqaWorkItem.notes) {
      try {
        const endpoint = `/documents/download/${note}/v1`;
        const content = await api.request(endpoint);
        const text = await content.text();
        const htmlNote = documentUtils.decoratedNoteFactory({
          description: '',
          content: text,
          filename: 'Document',
          type: cqaWorkItem.serviceLine,
          //One of these ids may not be right
          document_id: note,
          id: note,
          filetype: 'text/plain',
          benchmode: commonEnums.BenchMode.GRADE
        });

        if (cqaWorkItem.serviceLine.toLowerCase() == 'surgery') {
          htmlNote.content = collapseWhitespace(htmlNote.content);
        }

        notes.push(htmlNote);
      } catch (e) {
        console.log(
          `Cases: error fetching document with id : ${note} for workItem ${cqaWorkItem.id}`,
          e
        );
      }
    }
    return notes;
  }
);

const createCQATransaction = createAsyncThunk<
  Accelerate.CQATransaction,
  Accelerate.CreateCQATransactionParams,
  { rejectValue: null }
>(GradeBenchPanel.CREATE_CQA_TRANSACTION, async (args, { dispatch }) => {
  const url = `/api/cqa-service/v1/invoice/${args.invoiceId}/transaction`;
  return await api
    .requestJSON<Accelerate.CQATransaction>(url, {
      method: 'POST'
    })
    .catch(error => {
      console.error('Failed to createCQATransaction by API call', error);
      return null;
    });
});

const deleteCQATransaction = createAsyncThunk<
  boolean,
  Accelerate.DeleteCQATransactionParams,
  { rejectValue: null }
>(GradeBenchPanel.DELETE_CQA_TRANSACTION, async (args, { dispatch }) => {
  if (args.original) {
    return false;
  }

  const url = `/api/cqa-service/v1/transaction/${args.transactionId}`;
  return await api
    .requestJSON<void>(url, {
      method: 'DELETE'
    })
    .then(() => true)
    .catch(error => {
      console.error('Failed to deleteCQATransaction by API call', error);
      return false;
    });
});

//This action may not be GradeBench specific however it is currently only called for grading ui
const launchAssociatedWorkItemInGradeBench = createAsyncThunk<
  null,
  Accelerate.LoadAssociatedWorkItemParams,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.LOAD_ASSOCIATED_WORKITEM, async (params, { dispatch }) => {
  const { cursorAPIContext, sdWorkitem } = params;

  const launchConfig: CMxCommonApp.LaunchWorkItemConfig = {
    serviceLine: sdWorkitem.data.serviceLine,
    caseId: sdWorkitem.data.case_id,
    processId: sdWorkitem.data.cmx_process_id,
    workitemId: sdWorkitem.data.case_id,
    sessionId: cursorAPIContext.sessionId,
    qaCollectionUuid: (sdWorkitem.data as any).qa_collection_id,

    //From capabilities.cursorApiContext
    servicedeskMode: cursorAPIContext.servicedeskMode,
    worklistContext: cursorAPIContext.worklistContext,
    isCodingQuality: cursorAPIContext.worklistContext?.isCodingQuality,
    isQualityAssessment: cursorAPIContext.worklistContext?.isQualityAssessment,
    hasAmplifyCaseRunV2Access:
      cursorAPIContext.worklistContext?.hasAmplifyCaseRunV2Access,
    cursorAPIContext: cursorAPIContext,
    isMergedCase: cursorAPIContext.worklistContext?.isMergedCase
  };

  dispatch(loadWorkitemInAmplify(launchConfig));
  return null;
});

const submitCQAWorkitem = createAsyncThunk<
  null,
  Accelerate.SubmitCQAWorkitemParams,
  { rejectValue: CMxCommonApp.CMxError }
>(GradeBenchPanel.SUBMIT_CQA_WORKITEM, async (params, { dispatch }) => {
  const { cursorAPIPayload, status } = params;
  const workitemId = cursorAPIPayload.cursorAPIContext.workitemId;

  try {
    await api.requestJSON<Accelerate.CQAWorkItem>(
      `/api/cqa-service/v1/grader-workitem/${workitemId}`,
      {
        method: HttpMethods.PUT,
        body: JSON.stringify({
          status: status
        })
      }
    );

    dispatch(showSubmittedFeedback());

    const { cursorAPIContext } = cursorAPIPayload;
    if (cursorAPIContext.worklistContext?.query == null) {
      const url = buildQualityAssessmentWorklistPath(cursorAPIContext);
      dispatch(navigationIntent({ intent: 'CASE_DETAIL', url }));
    } else {
      dispatch(
        loadNextOrPreviousCase({
          cursorAPIPayload: cursorAPIPayload,
          status: commonEnums.FeedbackMeta.SUCCESS
        })
      );
    }

    return null;
  } catch (e) {
    dispatch(
      amplifyFeedback({
        message: 'There was an issue submitting this workitem.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );
  }
});

export type ResolveCQAWorkitemResult = {
  validationErrors: CQA.ValidationErrorDto[];
  skipped: boolean;
};

const resolveCQAWorkitem = createAsyncThunk<
  ResolveCQAWorkitemResult,
  void,
  { rejectValue: unknown; state: CMx.ApplicationState }
>(
  GradeBenchPanel.RESOLVE_CQA_WORKITEM,
  async (_params, { dispatch, getState }) => {
    const empty = { validationErrors: [] as CQA.ValidationErrorDto[], skipped: true };
    const state = getState();
    if (!state?.gradeBenchPanel) return empty;
    
    const rubricVersionId =
      state.gradeBenchPanel.qaRule?.data?.project_configuration?.rubricVersionId;
    if (rubricVersionId == null) {
      return empty;
    }

    try {
      const input = gradeBenchStateToResolverInput(state.gradeBenchPanel);
      const result = await api.requestJSON<CQA.ResolverExecution>(
        '/api/cqa-service/v1/resolver/resolve',
        {
          method: HttpMethods.POST,
          body: JSON.stringify({ rubricVersionId, input })
        }
      );
      const validationErrors = result.validationErrors ?? [];
      return { validationErrors, skipped: false };
    } catch (e){  
      console.error('Error resolving CQA workitem', e);
      return empty;
    }
  }
);

const deleteCQACode = createAsyncThunk<
  boolean,
  Accelerate.DeleteCQACodeParams,
  { rejectValue: null }
>(GradeBenchPanel.DELETE_CQA_CODE, async (args, { dispatch }) => {
  if (args.code.original) {
    return false;
  }

  const url = `/api/cqa-service/v1/${args.codeType}/${args.code.id}`;
  return await api
    .requestJSON<void>(url, {
      method: 'DELETE'
    })
    .then(() => true)
    .catch(error => {
      console.error('Failed to deleteCQACode by API call', error);
      return false;
    });
});

//Actions currently not called directly from a component, only dispatched by other actions
const ReduxActions = {
  addModifier,
  addDiagnosis,
  addProcedure,
  updateOrAddAssessment
};

const fetchCodeDescriptions = createAsyncThunk<
  CMxAPI.CodeResponse,
  Accelerate.CQAGraderWorkItem,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  GradeBenchPanel.FETCH_CQA_CODE_DESCRIPTIONS,
  async (cqaGraderWorkItem, { dispatch }) => {
    const invoices = cqaGraderWorkItem.invoices;
    const healthSystemTenantId =
      cqaGraderWorkItem.workItem.healthSystemTenantId;

    if (invoices.length) {
      try {
        const generateCodeRequests = (
          items: any[],
          codeType: any,
          dictionaryName: any
        ): CMxAPI.CodeRequest[] =>
          items.map(item => ({
            code: item.code,
            codeType,
            effectiveDate: '2022-07-08',
            dictionaryName,
            columnName: 'Code'
          }));

        const codeRequests: CMxAPI.CodeRequest[] = invoices.flatMap(invoice =>
          invoice.transactions.flatMap(tx => [
            ...generateCodeRequests(tx.procedures, 'CPT', 'CPT Codes'),
            ...generateCodeRequests(tx.diagnoses, 'ICD', 'ICD Codes'),
            ...generateCodeRequests(tx.modifiers, 'MODIFIER', 'Modifiers')
          ])
        );

        const uniqueRequests = uniqBy(
          codeRequests,
          (code: CMxAPI.CodeRequest) => `${code.code}-${code.effectiveDate}`
        );

        const url = `/dictionary/codes/description/v2?tenantId=${healthSystemTenantId}`;
        const descriptions = await api.requestJSON<CMxAPI.CodeResponse>(url, {
          body: JSON.stringify(uniqueRequests),
          method: 'POST'
        });

        const normalizeDescriptions = dictionaries.normalizeDescriptions(
          descriptions
        );

        return normalizeDescriptions;
      } catch (e) {
        console.log(e);
      }
    }
    return {};
  }
);

const retrieveAssociatedWorkitems = createAsyncThunk<
  CMxAPI.WorkItem[],
  number,
  { rejectValue: null }
>(
  GradeBenchPanel.RETRIEVE_ASSOCIATED_WORKITEMS,
  async (workItemId, { dispatch }) => {
    try {
      const cqaWorkItem = await api.requestJSON<Accelerate.CQAWorkItem>(
        `/api/cqa-service/v1/workitem/${workItemId}`,
        {
          method: HttpMethods.GET
        }
      );

      if (!cqaWorkItem?.graders) {
        console.error('No graders found in workitem response');
        return [];
      }

      const url = '/api/service-desk/bff';
      const caseIds = cqaWorkItem.graders.map(grader => grader.sdCaseId);

      if (caseIds.length <= 1) {
        //If the workitem has only 1 grader then it is not a multi-user workitem, so we dont need
        return [];
      }

      const query = {
        operationName: 'SearchWorkItems',
        variables: {
          request: {
            combinator: 'OR',
            filters: [
              {
                key: 'workitem.case_id',
                operation: 'in',
                value: caseIds
              }
            ],
            pagination: {
              limit: 50,
              offset: 0
            },
            groupByCQAWorkItemId: true
          }
        },
        query:
          'mutation SearchWorkItems($request: SearchWorkItemRequest!) { \nSearchWorkItems(request: $request) { \n data \n total \n__typename \n} \n}'
      };

      try {
        const response = await api.requestJSON<CMxAPI.WorkItemResponse>(url, {
          body: JSON.stringify(query),
          method: 'POST'
        });

        return response.data.SearchWorkItems.data;
      } catch (error) {
        console.error('Failed to SearchQarule in fetchQaRule fn', error);
      }
    } catch (error) {
      console.error('Failed to retrieve associated workitems:', error);
    }
  }
);

const fetchWorkerInfoForUser = createAsyncThunk<
  CMxAPI.WorkerQueryResponse,
  CMxCommonApp.Context,
  { rejectValue: null }
>(
  GradeBenchPanel.FETCH_WORKER_INFO_FOR_USER,
  async (userContext, { getState }) => {
    const state = getState() as any;
    const fullContext = state.ui?.context || userContext;
    
    const url = '/api/service-desk/bff';
    // Handle both full context (with .user) and activeContext (with .userId directly or .user)
    const userId = fullContext?.user?.userId || fullContext?.userId || '';
    const tenantId = fullContext?.activeContext?.tenantId || fullContext?.tenantId || '';
    
    const query = {
      operationName: 'QueryWorker',
      variables: {
        workerQuery: {
          query: `Select * from worker where user_id='${userId}' and subtenant_id = '${tenantId}' and status='ACTIVE';`
        }
      },
      query:
        'mutation QueryWorker($workerQuery: workerQuery!) { \nQueryWorker(workerQuery: $workerQuery)\n}'
    };

    try {
      const response = await api.requestJSON<CMxAPI.WorkerQueryResponse>(url, {
        body: JSON.stringify(query),
        method: 'POST'
      });

      return response;
    } catch (error) {
      console.error('Failed to fetch worker info:', error);
      return null;
    }
  }
);

const getGraderWorkItemActivity = createAsyncThunk<
  { activity: CQA.ActivityEntry[]; comments: AmplifyAPI.CoderNoteRow[] },
  string,
  { rejectValue: CMxCommonApp.CMxError }
>(
  GradeBenchPanel.FETCH_GRADER_WORK_ITEM_ACTIVITY,
  async (sdCaseId, { dispatch }) => {
    const graderWorkItemActivityPromise = api.requestJSON<CQA.ActivityEntry[]>(
      `/api/cqa-service/v1/activity/grader-workitem/${sdCaseId}`
    );

    const lookupRefName = CoderNotesLookupTypeName['CQA_CODER_COMMENT'];
    const coderCommentsPath = `/coding-api/coder_notes/v1/?${lookupRefName}=${sdCaseId}&lookup_type=CQA_CODER_COMMENT`;
    const feedbackCommentsPath = `/coding-api/coder_notes/v1/?${lookupRefName}=${sdCaseId}&lookup_type=CQA_FEEDBACK_COMMENT`;

    const coderCommentsPromise = api.requestJSON<AmplifyAPI.CoderNoteRow[]>(coderCommentsPath, {
      method: HttpMethods.GET
    });
    const feedbackCommentsPromise = api.requestJSON<AmplifyAPI.CoderNoteRow[]>(feedbackCommentsPath, {
      method: HttpMethods.GET
    });

    const [activity, coderComments, feedbackComments] = await Promise.all([
      graderWorkItemActivityPromise,
      coderCommentsPromise,
      feedbackCommentsPromise
    ]);
    const comments = [...coderComments, ...feedbackComments];

    return { activity, comments };
  }
);

const addEmptySpan = createAsyncThunk<
  CQA.SpanAnnotationDocument,
  CQA.SpanAnnotationDocument,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.ADD_EMPTY_SPAN,
  async (spanData) => {

    try {
      const response = await api.requestJSON<CQA.SpanAnnotationDocument>(
        `/api/cqa-service/v1/spans`,
        {
          method: HttpMethods.POST,
          body: JSON.stringify(spanData)
        }
      );

      // Map response spans back to spanData structure with updated ID
      const updatedSpans = spanData.spans.map((originalSpan, index) => ({
        ...originalSpan,
        id: response[index]?.id ?? originalSpan.id
      }));

      return {
        ...spanData,
        spans: updatedSpans
      };
    } catch (error) {
      console.error('Failed to add empty span:', error);
      throw error;
    }
  }
);

const attachCodeToSpan = createAsyncThunk<
  CQA.CodeSpan,
  CQA.AttachCodeToSpanParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.ATTACH_CODE_TO_SPAN,
  async (params) => {
    try {
      const response = await api.requestJSON<CQA.CodeSpan>(
        `/api/cqa-service/v1/spans/${params.spanId}/attach-code`,
        {
          method: HttpMethods.POST,
          body: JSON.stringify({
            codeId: params.codeId,
            codeType: params.codeType,
            caseId: params.caseId,
            documentId: params.documentId
          })
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to attach code to span:', error);
      throw error;
    }
  }
);

const detachCodeFromSpan = createAsyncThunk<
  void,
  CQA.DetachCodeFromSpanParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.DETACH_CODE_FROM_SPAN,
  async (params) => {
    try {
      await api.requestJSON<void>(
        `/api/cqa-service/v1/spans/${params.spanId}/detach-code?code_id=${params.codeId}&code_type=${params.codeType}`,
        {
          method: HttpMethods.DELETE
        }
      );
    } catch (error) {
      console.error('Failed to detach code from span:', error);
      throw error;
    }
  }
);

const deleteSpan = createAsyncThunk<
  void,
  CQA.DeleteSpanParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.DELETE_SPAN,
  async (params) => {
    try {
      await api.requestJSON<void>(
        `/api/cqa-service/v1/spans/${params.spanId}`,
        {
          method: HttpMethods.DELETE
        }
      );
    } catch (error) {
      console.error('Failed to delete span:', error);
      throw error;
    }
  }
);

const clearRubricVersion = createAction(GradeBenchPanel.CLEAR_RUBRIC_VERSION);

const setValidationResult = createAction<{ isValid: boolean; errors: string[] } | null>('SET_VALIDATION_RESULT');

const setCaseLevelComplete = createAction<boolean | null>('SET_CASE_LEVEL_COMPLETE');

const clearDownstreamAttributes = createAction<CQA.ClearDownstreamAttributesParams>(
  GradeBenchPanel.CLEAR_DOWNSTREAM_ATTRIBUTES
);

const fetchRubricVersion = createAsyncThunk<
  CQA.FullRubricVersion,
  CQA.FetchRubricVersionParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.FETCH_RUBRIC_VERSION,
  async (params) => {
    try {
      const response = await api.requestJSON<CQA.FullRubricVersion>(
        `/api/cqa-service/v1/rubric-versions/${params.versionId}/full`,
        {
          method: HttpMethods.GET
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch rubric version:', error);
      throw error;
    }
  }
);

const updateAttribute = createAsyncThunk<
  CQA.Attribute,
  CQA.UpdateAttributeParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.UPDATE_ATTRIBUTE,
  async (params) => {
    const hasAnswerDisplayText =
      params.answerDisplayText != null && params.answerDisplayText.trim() !== '';
    const hasAnswerValueId =
      params.answerValueId != null;
    const hasSpanAnnotationIds =
      params.spanAnnotationIds != null && params.spanAnnotationIds.length > 0;

    // Exactly one answer field must be provided
    const providedCount = (hasAnswerDisplayText ? 1 : 0) + (hasAnswerValueId ? 1 : 0) + (hasSpanAnnotationIds ? 1 : 0);
    if (providedCount !== 1) {
      throw new Error('Exactly one of answerValueId, answerDisplayText, or spanAnnotationIds must be provided');
    }

    let body: Record<string, unknown>;
    if (hasAnswerDisplayText) {
      body = { questionId: params.questionId, answerDisplayText: params.answerDisplayText };
    } else if (hasSpanAnnotationIds) {
      body = { questionId: params.questionId, spanAnnotationIds: params.spanAnnotationIds };
    } else {
      body = { questionId: params.questionId, answerId: params.answerValueId };
    }

    const spanScopeAttributeId = params.spanScopeAttributeId ?? params.parentQuestionId;
    if (spanScopeAttributeId != null) {
      body.spanScopeAttributeId = spanScopeAttributeId;
    }

    try {
      const response = await api.requestJSON<CQA.Attribute>(
        `/api/cqa-service/v1/attributes/${params.entityType}/${params.entityId}`,
        {
          method: HttpMethods.POST,
          body: JSON.stringify(body)
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to update attribute:', error);
      throw error;
    }
  }
);

const fetchAttributes = createAsyncThunk<
  CQA.Attribute[],
  CQA.FetchAttributesParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.FETCH_ATTRIBUTES,
  async (params) => {
    const spanScopeAttributeId = params.spanScopeAttributeId ?? params.parentQuestionId;
    const query = spanScopeAttributeId != null
      ? `?spanScopeAttributeId=${spanScopeAttributeId}`
      : '';
    try {
      const response = await api.requestJSON<CQA.Attribute[]>(
        `/api/cqa-service/v1/attributes/${params.entityType}/${params.entityId}${query}`,
        {
          method: HttpMethods.GET
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch attributes:', error);
      throw error;
    }
  }
);

const deleteAttribute = createAsyncThunk<
  void,
  CQA.DeleteAttributeParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.DELETE_ATTRIBUTE,
  async (params) => {
    const spanScopeAttributeId = params.spanScopeAttributeId ?? params.parentQuestionId;
    const query = spanScopeAttributeId != null
      ? `?spanScopeAttributeId=${spanScopeAttributeId}`
      : '';
    try {
      await api.requestJSON<void>(
        `/api/cqa-service/v1/attributes/${params.entityType}/${params.entityId}/${params.questionId}${query}`,
        {
          method: HttpMethods.DELETE
        }
      );
    } catch (error) {
      console.error('Failed to delete attribute:', error);
      throw error;
    }
  }
);

/**
 * Fetches all attributes for a case (case-level + all code-level) in a single action.
 * This is called once when a case is loaded to avoid multiple individual fetches.
 */
const fetchAllAttributes = createAsyncThunk<
  CQA.AllAttributesResult,
  CQA.FetchAllAttributesParams,
  { rejectValue: CQAError }
>(
  GradeBenchPanel.FETCH_ALL_ATTRIBUTES,
  async (params) => {
    const { sdCaseId, invoices, looseSpans } = params;

    // Collect all code IDs with their entity types
    const codeEntities: { entityId: number; entityType: CQA.AttributeEntityType }[] = [];
    invoices.forEach(invoice => {
      invoice.transactions.forEach(transaction => {
        transaction.procedures.forEach(p =>
          codeEntities.push({ entityId: p.id, entityType: 'code_procedure' }));
        transaction.diagnoses.forEach(d =>
          codeEntities.push({ entityId: d.id, entityType: 'code_diagnosis' }));
        transaction.modifiers.forEach(m =>
          codeEntities.push({ entityId: m.id, entityType: 'code_modifier' }));
      });
    });

    // Collect all unique span IDs from code spans and loose spans
    const spanIds = new Set<number>();
    invoices.forEach(invoice => {
      invoice.transactions.forEach(transaction => {
        [...(transaction.procedures || []), ...(transaction.diagnoses || []), ...(transaction.modifiers || [])].forEach(code => {
          code.spans?.forEach(span => spanIds.add(span.id));
        });
      });
    });
    (looseSpans || []).forEach(span => spanIds.add(span.id));
    const spanEntities = Array.from(spanIds);

    // Fetch case attributes, all code attributes, and all span attributes in parallel
    const [caseAttributes, ...restResults] = await Promise.all([
      api.requestJSON<CQA.Attribute[]>(
        `/api/cqa-service/v1/attributes/case/${sdCaseId}`,
        { method: HttpMethods.GET }
      ).catch(() => [] as CQA.Attribute[]),
      ...codeEntities.map(({ entityId, entityType }) =>
        api.requestJSON<CQA.Attribute[]>(
          `/api/cqa-service/v1/attributes/${entityType}/${entityId}`,
          { method: HttpMethods.GET }
        ).catch(() => [] as CQA.Attribute[])
      ),
      ...spanEntities.map(spanId =>
        api.requestJSON<CQA.Attribute[]>(
          `/api/cqa-service/v1/attributes/span/${spanId}`,
          { method: HttpMethods.GET }
        ).catch(() => [] as CQA.Attribute[])
      )
    ]);

    const codeAttributeResults = restResults.slice(0, codeEntities.length);
    const spanAttributeResults = restResults.slice(codeEntities.length);

    // Build code attributes map: codeId -> attributes[]
    const codeAttributesMap: Record<number, CQA.Attribute[]> = {};
    codeEntities.forEach((entity, idx) => {
      codeAttributesMap[entity.entityId] = codeAttributeResults[idx];
    });

    // Build span attributes map: spanId -> attributes[]
    const spanAttributesMap: Record<number, CQA.Attribute[]> = {};
    spanEntities.forEach((spanId, idx) => {
      spanAttributesMap[spanId] = spanAttributeResults[idx];
    });

    return { caseAttributes, codeAttributesMap, spanAttributesMap };
  }
);

function revalidateWorkItem() {
  return async (dispatch: any, getState: () => any) => {
    const sdCaseId = getState().gradeBenchPanel?.invoices?.[0]?.sdCaseId;
    if (!sdCaseId) return;
    try {
      const validation = await validateWorkflow(sdCaseId);
      dispatch(setValidationResult(validation));
    } catch (error) {
      console.error('Revalidation failed:', error);
    }
  };
}

const addUserActivityComment = createAsyncThunk<
  void,
  { sdCaseId: string; comment: string },
  { rejectValue: CMxCommonApp.CMxError }
>(
  GradeBenchPanel.ADD_USER_ACTIVITY_COMMENT,
  async ({ sdCaseId, comment }, { dispatch }) => {
    const lookupRefName = CoderNotesLookupTypeName['CQA_CODER_COMMENT'];
    const path = `/coding-api/coder_notes/v1/?${lookupRefName}=${sdCaseId}&lookup_type=CQA_CODER_COMMENT`;

    await api.request(path, {
      method: HttpMethods.POST,
      body: JSON.stringify({
        note_content: comment
      })
    });

    await dispatch(getGraderWorkItemActivity(sdCaseId));
  }
);

export {
  gradeUpdate,
  fetchRiskCategories,
  addCode,
  removeCode,
  fetchQaRule,
  resequenceCode,
  loadCQAItemFromURL,
  getDocumentsAsHtmlFromCQAWorkItem,
  ReduxActions,
  fetchCodeDescriptions,
  createCQATransaction,
  deleteCQATransaction,
  deleteCQACode,
  submitCQAWorkitem,
  resolveCQAWorkitem,
  retrieveAssociatedWorkitems,
  launchAssociatedWorkItemInGradeBench,
  fetchWorkerInfoForUser,
  getGraderWorkItemActivity,
  addEmptySpan,
  attachCodeToSpan,
  detachCodeFromSpan,
  deleteSpan,
  clearRubricVersion,
  fetchRubricVersion,
  updateAttribute,
  fetchAttributes,
  deleteAttribute,
  fetchAllAttributes,
  addUserActivityComment,
  clearDownstreamAttributes,
  evaluateWorkflow,
  validateWorkflow,
  setValidationResult,
  setCaseLevelComplete,
  revalidateWorkItem
};

/**
 * Calls the workflow evaluation API to evaluate system questions and determine
 * visible questions, filtered answers, and autofilled answers.
 * This is a plain async function (not a Redux thunk) so it can be used as evaluateFn.
 */
async function evaluateWorkflow(request: {
  workflowId: number;
  attributes: { questionId: number; answerValueId?: number; answerDisplayText?: string; spanAnnotationIds?: number[] }[];
  codeContext?: {
    original: boolean | null;
    code: string;
    sequenceNumber: number;
    codeType: string;
  };
  workItemId?: number;
  sdCaseId?: string;
  codeId?: string;
  spanScopeAttributeId?: number;
  parentQuestionId?: number;
  spanId?: number;
  rubricVersionId?: number;
  caseAttributes?: { questionId: number; answerValueId: number }[];
}): Promise<any> {
  const result: any = await api.requestJSON(
    `/api/cqa-service/v1/workflows/evaluate`,
    {
      method: HttpMethods.POST,
      body: JSON.stringify(request),
    }
  );

  if (request.sdCaseId) {
    try {
      const validation = await validateWorkflow(request.sdCaseId);
      result.validation = validation;
    } catch (error) {
      console.error('Validation failed after evaluation:', error);
    }
  }

  return result;
}

/**
 * Calls the workflow validation API to validate all required questions are answered.
 * This is a plain async function (not a Redux thunk) so it can be used as validateFn.
 *
 * @param workItemId - The work item ID (t_work_item.id) to validate
 * @returns Promise resolving to {isValid: boolean, errors: string[]}
 */
async function validateWorkflow(workItemId: string): Promise<{ isValid: boolean; errors: string[] }> {
  return api.requestJSON(
    `/api/cqa-service/v1/work-items/${workItemId}/validate`,
    {
      method: HttpMethods.GET
    }
  );
}
